import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import kotlinx.browser.window

// Fáze 0 skeleton: tři obrazovky, hash-free path routing (SPA fallback zajišťuje webpack.config.d).
// Živá data z bowlingligy se napojí ve fázi 2; členská sekce (auth + vlastní data) ve fázi 3.

sealed class Screen {
    data object Home : Screen()
    data object Players : Screen()
    data class PlayerDetail(val playerId: String) : Screen()
    data object Children : Screen()
    data class ChildDetail(val childId: String) : Screen()
    data object Members : Screen()
}

private fun pageTitle(screen: Screen): String = when (screen) {
    Screen.Home -> "Sunny Gang Bowling"
    Screen.Players -> "Hráči"
    is Screen.PlayerDetail -> "Hráč"
    Screen.Children -> "Dětská sekce"
    is Screen.ChildDetail -> "Dítě"
    Screen.Members -> "Členská sekce"
}

private fun pathForScreen(screen: Screen): String = when (screen) {
    Screen.Home -> "/"
    Screen.Players -> "/hraci"
    is Screen.PlayerDetail -> "/hrac/${screen.playerId}"
    Screen.Children -> "/deti"
    is Screen.ChildDetail -> "/dite/${screen.childId}"
    Screen.Members -> "/clenska-sekce"
}

private fun screenForPath(path: String): Screen {
    val trimmed = path.trim('/')
    return when {
        trimmed == "hraci" -> Screen.Players
        trimmed == "deti" -> Screen.Children
        trimmed == "clenska-sekce" -> Screen.Members
        trimmed.startsWith("hrac/") -> Screen.PlayerDetail(trimmed.removePrefix("hrac/"))
        trimmed.startsWith("dite/") -> Screen.ChildDetail(trimmed.removePrefix("dite/"))
        else -> Screen.Home
    }
}

fun main() {
    renderComposable(rootElementId = "root") { App() }
}

@Composable
fun App() {
    var screen by remember { mutableStateOf(screenForPath(window.location.pathname)) }
    var isPop by remember { mutableStateOf(false) }
    var linkedReadOnlyPassword by remember { mutableStateOf(
        if (screenForPath(window.location.pathname) == Screen.Members) takeReadOnlyAccessFromUrl() else null
    ) }
    var token by remember { mutableStateOf(if (linkedReadOnlyPassword != null) null else loadSessionToken()) }
    // Recovery odkaz z e-mailu přišel s tokenem v URL hashi -> zobraz nastavení nového hesla.
    var recovery by remember { mutableStateOf(recoveryFromUrl()) }

    LaunchedEffect(screen) {
        if (isPop) {
            isPop = false
        } else {
            val path = pathForScreen(screen)
            if (window.location.pathname != path) window.history.pushState(null, "", path)
        }
    }

    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            isPop = true
            screen = screenForPath(window.location.pathname)
        }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }

    val rec = recovery
    if (rec != null) {
        LaunchedEffect(Unit) { clearUrlHash() } // tokeny/chybu z adresy pryč (reload flow nespustí znovu)
        Div(attrs = { classes("app-shell") }) {
            Div(attrs = { classes("content") }) {
                Div(attrs = { classes("topbar") }) { H2 { Text("Sunny Gang Bowling") } }
                Div(attrs = { classes("page") }) {
                    ResetPasswordScreen(rec, onDone = { newToken ->
                        if (newToken != null) { token = newToken; saveSessionToken(newToken) }
                        recovery = null
                        screen = Screen.Members
                    })
                }
            }
        }
        return
    }

    Div(attrs = { classes("app-shell") }) {
        Sidebar(screen) { screen = it }
        Div(attrs = { classes("content") }) {
            Div(attrs = { classes("topbar") }) {
                Div {
                    Span(attrs = { classes("page-eyebrow") }) { Text("Sunny Gang / Bowling") }
                    H2 { Text(pageTitle(screen)) }
                }
                Span(attrs = { classes("topbar-mark"); attr("aria-hidden", "true") }) { Text("SG") }
            }
            Div(attrs = { classes("page") }) {
                when (val s = screen) {
                    Screen.Home -> HomeScreen()
                    Screen.Players -> PlayersScreen(onOpenPlayer = { screen = Screen.PlayerDetail(it) })
                    is Screen.PlayerDetail -> PlayerDetailScreen(playerId = s.playerId, onBack = { screen = Screen.Players })
                    Screen.Children -> ChildrenScreen(onOpenChild = { screen = Screen.ChildDetail(it) })
                    is Screen.ChildDetail -> ChildDetailScreen(childId = s.childId, onBack = { screen = Screen.Children })
                    Screen.Members -> MembersScreen(
                        token = token,
                        linkedReadOnlyPassword = linkedReadOnlyPassword,
                        onLinkedAccessConsumed = { linkedReadOnlyPassword = null },
                        onToken = { token = it; saveSessionToken(it) },
                    )
                }
            }
        }
    }
}

@Composable
fun Sidebar(screen: Screen, onChange: (Screen) -> Unit) {
    Div(attrs = { classes("sidebar") }) {
        Div(attrs = { classes("brand") }) {
            Div(attrs = { classes("brand-ball"); attr("aria-hidden", "true") }) {}
            H1 {
                Span(attrs = { classes("brand-title") }) { Text("Sunny Gang") }
                Text(" ")
                Span(attrs = { classes("brand-subtitle") }) { Text("Bowling") }
            }
        }
        Div(attrs = { classes("nav") }) {
            NavButton("Domů", screen == Screen.Home, "home") { onChange(Screen.Home) }
            NavButton("Hráči", screen == Screen.Players || screen is Screen.PlayerDetail, "players") { onChange(Screen.Players) }
            NavButton("Dětská sekce", screen == Screen.Children || screen is Screen.ChildDetail, "children") { onChange(Screen.Children) }
            NavButton("Členská sekce", screen == Screen.Members, "members") { onChange(Screen.Members) }
        }
    }
}

@Composable
fun NavButton(label: String, active: Boolean, icon: String = "home", onClick: () -> Unit) {
    Button(attrs = {
        if (active) classes("active")
        if (active) attr("aria-current", "page")
        onClick { onClick() }
    }) {
        Span(attrs = { classes("nav-icon", "nav-icon-$icon"); attr("aria-hidden", "true") }) {}
        Span { Text(label) }
    }
}
